import { createContext, useContext, useEffect, useState } from 'react';

import Swal from 'sweetalert2';

import Api from '../Api';

const baseUrl = '/painel';
const alertSoundUrl = `${process.env.PUBLIC_URL}/assets/audio/siren-iot.mp3`;

const buildIotsNcAlertHtml = (list) => {
  let html = `
    Há uma ou mais NC(s) em aberto relacionadas aos seus dispositivos IOT
    <p class="mt-5 mb-5">----------- Relação da(s) NC(s): -----------</p>
    <div class="custom-swal-nc-list">
  `;

  list.forEach((item) => {
    if (parseInt(item.count_nc_iots) > 0) {
      const unity = item.info_name || '';

      const ncIds = item.nc_ids_list.split(',').map((id) => id.trim());
      const ncCreateds = item.nc_date_created_list
        .split(',')
        .map((created) => created.trim());
      const maintenanceTypes = item.maintenance_type_names
        .split(',')
        .map((type) => type.trim());

      html += `<p class="my-4"><b>${unity}</b></p>`;

      for (let i = 0; i < ncIds.length; i++) {
        const ncId = ncIds[i];
        const ncCreated = ncCreateds[i];
        const maintenanceType = maintenanceTypes[i] || '';

        html += `<p class="text-start"><b><a href="/painel/dashboard/maintenance/${ncId}">NC - ${ncId.toString().padStart(5, '0')}</a></b> (${ncCreated}) - ${maintenanceType}</p>`;
      }
    }
  });

  html += `</div>`;

  return html;
};

const useCreateIotNcAlertPopup = () => {
  const [audio] = useState(() => {
    const el = new Audio(alertSoundUrl);
    el.preload = 'auto';
    return el;
  });

  // Browsers only let an <audio> element autoplay-with-sound once it has
  // already played from a real click/key/touch. The poll in Header.js (the
  // only caller that plays sound) fires on its own, with no gesture at that
  // moment, so this "blesses" the element on whichever gesture happens first.
  useEffect(() => {
    const blessOnFirstInteraction = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => audio.pause())
        .catch(() => {})
        .finally(() => {
          audio.currentTime = 0;
          audio.muted = false;
        });
    };

    const options = { once: true, capture: true };
    document.addEventListener('click', blessOnFirstInteraction, options);
    document.addEventListener('keydown', blessOnFirstInteraction, options);
    document.addEventListener('touchstart', blessOnFirstInteraction, options);

    return () => {
      document.removeEventListener('click', blessOnFirstInteraction, options);
      document.removeEventListener('keydown', blessOnFirstInteraction, options);
      document.removeEventListener(
        'touchstart',
        blessOnFirstInteraction,
        options
      );
    };
  }, [audio]);

  const playAlertSound = () => {
    audio.currentTime = 0;
    audio
      .play()
      .catch((error) =>
        console.error('IoT NC alert sound failed to play:', error)
      );
  };

  const stopAlertSound = () => {
    audio.pause();
    audio.currentTime = 0;
  };

  const showIotNcAlertPopup = (list, { playSound = false } = {}) =>
    Swal.fire({
      title: 'Alerta de NC(s)',
      html: buildIotsNcAlertHtml(list),
      imageUrl: `${baseUrl}/assets/img/siren.gif`,
      imageWidth: 100,
      imageHeight: 100,
      allowOutsideClick: false,
      confirmButtonText: 'OK',
      customClass: {
        popup: 'custom-swal-popup',
      },
      didOpen: (popup) => {
        if (popup) popup.scrollTop = 0;
        if (playSound) playAlertSound();
      },
      preConfirm: () => {
        stopAlertSound();
      },
    });

  const verifyIotsNcsOpened = async () => {
    const res = await Api.getUnitysNCopenedIots(
      sessionStorage.getItem('client_id'),
      sessionStorage.getItem('userId')
    );

    if (res && res.some((item) => parseInt(item.count_nc_iots) > 0)) {
      showIotNcAlertPopup(res);
    }
  };

  return { showIotNcAlertPopup, verifyIotsNcsOpened };
};

const IotNcAlertContext = createContext(null);

export const IotNcAlertProvider = ({ children }) => (
  <IotNcAlertContext.Provider value={useCreateIotNcAlertPopup()}>
    {children}
  </IotNcAlertContext.Provider>
);

const useIotNcAlertPopup = () => useContext(IotNcAlertContext);

export default useIotNcAlertPopup;
